<script setup lang="ts">
import { ref, computed } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import type { Swiper as SwiperType } from "swiper";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { VehicleAlgoliaRecord } from "@/types";
import { generateVehicleImageAlt } from "@/helpers/vehicle";
import {
    getVehicleCardDefaultImageWidthForLayout,
    getVehicleCardImageSizesForLayout,
    getVehicleCardImageWidthsForLayout,
    getVehicleImageUrl,
    getVehicleImageSrcSet,
} from "@/helpers/imageKit";
import { ssrIsMobile } from "@/helpers/client";

const MAX_DISPLAY_PHOTOS = 5;

const props = withDefaults(
    defineProps<{
        vehicle: VehicleAlgoliaRecord;
        photos: Array<string>;
        url: string;
        eagerLoading?: boolean;
        photosCount?: number;
        imageLayout?: string;
    }>(),
    {
        eagerLoading: false,
        imageLayout: "default",
    },
);

const defaultImageWidth = computed(() =>
    getVehicleCardDefaultImageWidthForLayout(props.imageLayout),
);
const cardSrcSetWidths = computed(() =>
    getVehicleCardImageWidthsForLayout(props.imageLayout),
);
const cardImageSizes = computed(() =>
    getVehicleCardImageSizesForLayout(props.imageLayout),
);
const cardImageHeight = computed(() =>
    Math.round((defaultImageWidth.value * 11) / 16),
);
const isMobile = ref(ssrIsMobile());
const currentSlide = ref(0);
const swiperInstance = ref<SwiperType | null>(null);

const totalCount = computed(() => props.photosCount ?? props.photos.length);
const displayPhotos = computed(() => props.photos.slice(0, MAX_DISPLAY_PHOTOS));
const showNavigation = computed(() => displayPhotos.value.length > 1);
const showOverlay = computed(
    () =>
        displayPhotos.value.length >= MAX_DISPLAY_PHOTOS &&
        currentSlide.value === displayPhotos.value.length - 1,
);

const onSwiper = (swiper: any) => {
    swiperInstance.value = swiper;
};

const onSlideChange = (swiper: any) => {
    currentSlide.value = swiper.realIndex;
};

const next = () => {
    swiperInstance.value?.slideNext();
};

const prev = () => {
    swiperInstance.value?.slidePrev();
};

const swiperOptions = {
    modules: [Navigation],
    loop: false,
    slidesPerView: 1,
    spaceBetween: 0,
    centeredSlides: true,
    allowTouchMove: true,
    simulateTouch: true,
    touchStartPreventDefault: false,
    touchMoveStopPropagation: false,
    threshold: 10,
    grabCursor: !isMobile.value,
    followFinger: true,
    shortSwipes: true,
    longSwipes: true,
    longSwipesRatio: 0.5,
    longSwipesMs: 300,
    resistance: true,
    resistanceRatio: 0.85,
    effect: "slide",
    speed: 300,
    preventClicks: false,
    preventClicksPropagation: false,
    slideToClickedSlide: false,
};
</script>

<template>
    <div class="carousel-wrapper">
        <div>
            <swiper
                class="swiper-container"
                :options="swiperOptions"
                @swiper="onSwiper"
                @slide-change="onSlideChange"
            >
                <swiper-slide
                    v-for="(photo, index) in displayPhotos"
                    :key="index"
                    class="swiper-slide"
                >
                    <a :href="url" class="slide-link" data-attr="vehicle-card-link">
                        <img
                            :key="`img-${vehicle.id}-${index}`"
                            :width="defaultImageWidth"
                            :height="cardImageHeight"
                            :loading="
                                index === 0 && eagerLoading ? 'eager' : 'lazy'
                            "
                            :fetchpriority="
                                index === 0 && eagerLoading ? 'high' : 'low'
                            "
                            :src="
                                getVehicleImageUrl(vehicle, photo, {
                                    width: defaultImageWidth,
                                })
                            "
                            :srcset="
                                getVehicleImageSrcSet(
                                    vehicle,
                                    photo,
                                    cardSrcSetWidths,
                                )
                            "
                            :sizes="cardImageSizes"
                            decoding="async"
                            :alt="generateVehicleImageAlt(vehicle, index)"
                        />
                    </a>
                </swiper-slide>
            </swiper>

            <div v-if="showNavigation" class="navigation">
                <button
                    class="nav-button prev"
                    aria-label="Précédent"
                    tabindex="0"
                    @click="prev"
                >
                    <svg
                        class="carousel__icon"
                        viewBox="0 0 24 24"
                        role="img"
                        aria-label="Flèche gauche"
                    >
                        <title>Flèche gauche</title>
                        <path
                            d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"
                        ></path>
                    </svg>
                </button>
                <button
                    class="nav-button next"
                    aria-label="Suivant"
                    tabindex="0"
                    @click="next"
                >
                    <svg
                        class="carousel__icon"
                        viewBox="0 0 24 24"
                        role="img"
                        aria-label="Flèche droite"
                    >
                        <title>Flèche droite</title>
                        <path
                            d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"
                        ></path>
                    </svg>
                </button>
            </div>

            <div v-if="showNavigation" class="vehicle-card__photo-counter">
                <span>{{ currentSlide + 1 }}</span>/<span>{{ totalCount }}</span>
            </div>

            <div v-if="showOverlay" class="vehicle-card__overlay">
                <a
                    :href="url"
                    class="vehicle-card__overlay__cta button button--primary"
                    data-attr="vehicle-card-link"
                >
                    Voir le véhicule
                </a>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
:deep(.swiper) {
    width: 100%;
    height: 100%;
    margin-left: auto;
    margin-right: auto;
}

:deep(.swiper-slide) {
    text-align: center;
    font-size: 18px;
    background: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
}

:deep(.swiper-slide img) {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.carousel-wrapper {
    position: relative;
    &:hover {
        .navigation {
            opacity: 1;
            pointer-events: auto;
        }
    }

    @media (max-width: 768px) {
        &:hover {
            .navigation {
                opacity: 0;
                pointer-events: none;
            }
        }

        .navigation {
            display: none;
        }
    }
}

.swiper-container {
    aspect-ratio: 4/3;
    position: relative;
    width: 100%;
    height: 100%;
    touch-action: pan-y;
}

.swiper-slide {
    aspect-ratio: 4/3;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    width: 100%;
    height: 100%;
}

.slide-link {
    display: block;
    width: 100%;
    height: 100%;
}

img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    border-radius: 0.125rem;
    will-change: transform;
}

.navigation {
    box-sizing: border-box;
    bottom: 10px;
    position: absolute;
    left: 0;
    width: 100%;
    pointer-events: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 5;
    opacity: 0;
    transition: opacity 0.2s;
    padding: 0 0.5rem;
}

.nav-button {
    pointer-events: auto;
    background: rgb(232, 255, 46);
    border: none;
    border-radius: 2px;
    width: 30px;
    height: 30px;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s;
    color: #003c46;
    box-sizing: border-box;
    padding: 0;

    &:hover {
        background: #e4ff06;
    }
}

.nav-button svg,
.nav-button span,
.nav-button i {
    color: currentColor;
    fill: currentColor;
}

.vehicle-card__photo-counter {
    position: absolute;
    bottom: 0.75rem;
    right: 0.75rem;
    z-index: 4;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    font-size: 0.75rem;
    padding: 0.15rem 0.5rem;
    border-radius: 4px;
    font-variant-numeric: tabular-nums;
    pointer-events: none;
    line-height: 1.4;
}

.vehicle-card__overlay {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 0.5rem;
    background: linear-gradient(
        to top,
        rgba(0, 0, 0, 0.65) 0%,
        rgba(0, 0, 0, 0.2) 50%,
        transparent 100%
    );
    border-radius: 0.125rem;
    pointer-events: none;
}

.vehicle-card__overlay__cta {
    pointer-events: auto;
    position: relative;
    z-index: 5;
    font-size: 0.85rem;
    padding: 0.5rem 1.2rem;
    border-radius: 0.25rem;
    background-color: #e8ff2e;
    color: #1a1a1a;
    font-weight: 500;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: #d4eb1a;
    }
}
</style>
